import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';

import cx from 'classnames';
import { Button } from 'components';
import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { ContentRendererContext } from 'lane-shared/contexts';
import { hasPermission } from 'lane-shared/helpers';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import useFlag from 'lane-shared/hooks/useFlag';
import useUserDataContext from 'lane-shared/hooks/useUserDataContext';
import { FeatureFlag } from 'constants-flags';
import { FeatureNameEnum } from 'constants-content';
import { PERMISSION_KEYS } from 'constants-permissions';
import { useContentEditorContext } from 'contexts/ContentEditorContext';

import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { convertToUUID } from 'uuid-encoding';

import type { VisitorCustomFieldConfig } from 'graphql-query-contracts';
import type { VisitorManagementFeatureProperties, FeatureInstance } from 'application-types';

import { useVisitorCustomFieldConfigs } from 'domains/visitorManagement/hooks/useVisitorCustomFieldConfigs';

import { CustomFieldsModal } from './CustomFieldsModal';
import { CustomFieldRenderer } from './CustomFieldRenderer';

import styles from './CustomFieldsSection.scss';

type CustomFieldsSectionProps = {
  className?: string;
  isPreview?: boolean;
};

export function CustomFieldsSection({
  className,
  isPreview = false,
}: CustomFieldsSectionProps) {
  const { t } = useTranslation();
  const { editMode, content } = useContext(ContentRendererContext);
  const { onContentUpdated } = useContentEditorContext();
  const userDataContext = useUserDataContext();
  const channelAdminContext = useChannelAdminContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addedFields, setAddedFields] = useState<VisitorCustomFieldConfig[]>(
    []
  );
  const initializedRef = useRef(false);
  /** When the user saves an empty selection, `content` may lag behind local state; skip restoration until persisted IDs reflect empty. */
  const awaitingPersistedEmptyAfterClearRef = useRef(false);
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );

  const user = userDataContext?.user;
  const channel = channelAdminContext?.channel;

  const isAdmin =
    user?.isSuperUser ||
    hasPermission(
      user?.roles || [],
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );

  const organizationId = channel?._id ? convertToUUID(channel._id) : '';

  const vmFeature = useMemo(() => {
    const cf = content?.features?.find(
      (f: { type: string }) => f.type === FeatureNameEnum.VisitorManagement
    );

    return cf?.feature as VisitorManagementFeatureProperties | undefined;
  }, [content?.features]);

  const persistedCustomFieldIds = vmFeature?.customFieldIds;

  const { data: enabledCustomFields, loading, called, error, fetch: fetchCustomFields } =
    useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (
      customFieldsEnabled &&
      !initializedRef.current &&
      persistedCustomFieldIds?.length &&
      organizationId
    ) {
      fetchCustomFields(organizationId);
      initializedRef.current = true;
    }
  }, [customFieldsEnabled, persistedCustomFieldIds, organizationId, fetchCustomFields]);

  useEffect(() => {
    if (!persistedCustomFieldIds?.length) {
      awaitingPersistedEmptyAfterClearRef.current = false;
    }
  }, [persistedCustomFieldIds]);

  useEffect(() => {
    if (
      customFieldsEnabled &&
      enabledCustomFields.length > 0 &&
      persistedCustomFieldIds?.length &&
      addedFields.length === 0 &&
      !awaitingPersistedEmptyAfterClearRef.current
    ) {
      const persistedSet = new Set(persistedCustomFieldIds);
      const restored = enabledCustomFields.filter(f => persistedSet.has(f.id) && f.isEnabled);

      if (restored.length > 0) {
        setAddedFields(restored);
      }
    }
  }, [customFieldsEnabled, enabledCustomFields, persistedCustomFieldIds, addedFields.length]);

  const updateContentFeature = useCallback(
    (fieldIds: string[]) => {
      if (!onContentUpdated || !content?.features) return;

      const updatedFeatures = content.features.map(
        (cf: FeatureInstance): FeatureInstance => {
          if (cf.type === FeatureNameEnum.VisitorManagement) {
            return {
              ...cf,
              feature: {
                ...(cf.feature as VisitorManagementFeatureProperties),
                customFieldIds: fieldIds,
              },
            };
          }

          return cf;
        }
      );

      onContentUpdated({ features: updatedFeatures });
    },
    [onContentUpdated, content?.features]
  );

  const handleOpenModal = useCallback(() => {
    fetchCustomFields(organizationId);
    setIsModalOpen(true);
  }, [fetchCustomFields, organizationId]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSaveFields = useCallback(
    (fields: VisitorCustomFieldConfig[]) => {
      awaitingPersistedEmptyAfterClearRef.current = fields.length === 0;
      setAddedFields(fields);
      setIsModalOpen(false);
      updateContentFeature(fields.map(f => f.id));
    },
    [updateContentFeature]
  );

  const alreadyAddedFieldIds = useMemo(
    () => addedFields.map(f => f.id),
    [addedFields]
  );

  if (!customFieldsEnabled) {
    return null;
  }

  if (!editMode || !isAdmin || isPreview) {
    if (addedFields.length === 0) return null;

    return (
      <div
        className={styles.addedFieldsContainer}
        data-test="addedFieldsContainer"
      >
        <h3 className={styles.addedFieldsHeading}>
          {t(
            'web.admin.content.features.visitor.management.general.customFields.addedFieldsTitle'
          )}
        </h3>
        {addedFields.map(field => (
          <CustomFieldRenderer key={field.id} field={field} />
        ))}
      </div>
    );
  }

  return (
    <>
      {addedFields.length > 0 && (
        <div
          className={styles.addedFieldsContainer}
          data-test="addedFieldsContainer"
        >
          <h3 className={styles.addedFieldsHeading}>
            {t(
              'web.admin.content.features.visitor.management.general.customFields.addedFieldsTitle'
            )}
          </h3>
          {addedFields.map(field => (
            <CustomFieldRenderer key={field.id} field={field} />
          ))}
        </div>
      )}

      <div
        className={cx(styles.customFieldsSection, className)}
        data-test="customFieldsSection"
      >
        <Button
          variant="outlined"
          className={styles.addCustomFieldsButton}
          onClick={handleOpenModal}
          testId="addCustomFieldsButton"
          startIcon={
            <Icon
              name="plus"
              set={ICON_SET_FONTAWESOME}
              testId="addCustomFieldsIcon"
            />
          }
        >
          {t(
            'web.admin.content.features.visitor.management.general.customFields.addButton'
          )}
        </Button>
      </div>

      {isModalOpen && (
        <CustomFieldsModal
          isOpen={isModalOpen}
          customFields={enabledCustomFields}
          loading={loading}
          error={error}
          onClose={handleCloseModal}
          onSave={handleSaveFields}
          alreadyAddedFieldIds={alreadyAddedFieldIds}
          hasFetched={called}
        />
      )}
    </>
  );
}
